import { useAppContext } from "@/App";
import {
  CalendarDays, X, Upload, FileText, Trash2
} from "lucide-react";

export default function CalendarPanel({
  calendarEvents, calForm, setCalForm, onClose,
  onCreateEvent, onApproveLeave, onDeleteEvent, onToolboxUpload
}) {
  const { hubData } = useAppContext();
  const { drivers } = hubData;

  return (
    <div className="bg-slate-50/80 border-b border-blue-500/20 px-4 md:px-8 py-4 animate-slideInDown" data-testid="calendar-panel">
      <div className="max-w-4xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2">
            <CalendarDays className="w-4 h-4 text-blue-500" /> Hub Calendar
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900"><X className="w-4 h-4" /></button>
        </div>
        {/* Create Event Form */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-4">
          <select value={calForm.event_type} onChange={e => setCalForm(p => ({ ...p, event_type: e.target.value }))} data-testid="cal-type"
            className="bg-white border border-slate-200 rounded-xl px-3 py-2.5 text-xs font-bold text-slate-400 col-span-1">
            <option value="APPOINTMENT">Appointment</option>
            <option value="TRAINING">Training</option>
            <option value="TOOLBOX">Toolbox</option>
            <option value="LEAVE_REQUEST">Leave Request</option>
          </select>
          <input value={calForm.title} onChange={e => setCalForm(p => ({ ...p, title: e.target.value }))} placeholder="Title"
            className="bg-white border border-slate-200 rounded-xl px-3 py-2.5 text-xs font-bold text-slate-400 col-span-2" data-testid="cal-title" />
          <input type="date" value={calForm.date} onChange={e => setCalForm(p => ({ ...p, date: e.target.value }))}
            className="bg-white border border-slate-200 rounded-xl px-3 py-2.5 text-xs font-bold text-slate-400" data-testid="cal-date" />
          <input type="time" value={calForm.time} onChange={e => setCalForm(p => ({ ...p, time: e.target.value }))}
            className="bg-white border border-slate-200 rounded-xl px-3 py-2.5 text-xs font-bold text-slate-400" data-testid="cal-time" />
          <button onClick={onCreateEvent} disabled={!calForm.title || !calForm.date} data-testid="cal-create-btn"
            className="bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 text-white rounded-xl px-3 py-2.5 text-xs font-bold uppercase active:scale-95 transition-all">
            Add
          </button>
        </div>
        {/* Toolbox Upload */}
        <div className="mb-4">
          <label className="flex items-center gap-2 px-4 py-2.5 bg-white border border-dashed border-slate-200 rounded-xl cursor-pointer hover:border-blue-500/40 transition-all" data-testid="toolbox-upload">
            <Upload className="w-4 h-4 text-blue-400" />
            <span className="text-xs font-bold text-slate-400">Upload Toolbox Document (PDF)</span>
            <input type="file" accept=".pdf,.doc,.docx,.txt" className="hidden" onChange={e => e.target.files[0] && onToolboxUpload(e.target.files[0])} />
          </label>
        </div>
        {/* Events List */}
        <div className="space-y-2 max-h-[300px] overflow-y-auto">
          {calendarEvents.length === 0 ? (
            <p className="text-xs text-slate-400 font-bold text-center py-6 uppercase">No events yet</p>
          ) : calendarEvents.map(evt => (
            <div key={evt.id} className={`p-3 rounded-xl border flex items-center justify-between ${
              evt.event_type === "LEAVE_REQUEST" && evt.status === "PENDING" ? "border-red-500/30 bg-red-500/5" :
              evt.event_type === "TOOLBOX" ? "border-amber-200 bg-amber-50" :
              evt.event_type === "TRAINING" ? "border-blue-500/20 bg-blue-500/5" :
              "border-slate-200 bg-white"
            }`} data-testid={`cal-event-${evt.id}`}>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${
                    evt.event_type === "LEAVE_REQUEST" ? "bg-red-500/10 text-red-400" :
                    evt.event_type === "TOOLBOX" ? "bg-amber-50 text-amber-400" :
                    evt.event_type === "TRAINING" ? "bg-blue-500/10 text-blue-400" :
                    "bg-slate-100 text-slate-400"
                  }`}>{evt.event_type}</span>
                  <span className="text-xs font-bold text-slate-800">{evt.title}</span>
                  {evt.status === "PENDING" && <span className="text-[9px] font-bold text-red-400 animate-pulse">PENDING</span>}
                  {evt.status === "APPROVED" && evt.event_type === "LEAVE_REQUEST" && <span className="text-[9px] font-bold text-lime-600">APPROVED</span>}
                  {evt.status === "REJECTED" && <span className="text-[9px] font-bold text-red-500">REJECTED</span>}
                </div>
                <div className="flex items-center gap-3 mt-1">
                  <span className="text-[10px] text-slate-400 font-mono">{evt.date}{evt.time ? ` ${evt.time}` : ""}</span>
                  {evt.created_by && <span className="text-[10px] text-slate-400">by {evt.created_by}</span>}
                  {evt.file_name && <span className="text-[10px] text-amber-400 flex items-center gap-1"><FileText className="w-2.5 h-2.5" />{evt.file_name}</span>}
                </div>
              </div>
              <div className="flex gap-1.5 ml-2">
                {evt.event_type === "LEAVE_REQUEST" && evt.status === "PENDING" && (
                  <>
                    <button onClick={() => onApproveLeave(evt.id, "APPROVED")} data-testid={`approve-leave-${evt.id}`}
                      className="px-3 py-1.5 bg-lime-500 text-slate-950 rounded text-[10px] font-bold active:scale-95">Approve</button>
                    <button onClick={() => onApproveLeave(evt.id, "REJECTED")} data-testid={`reject-leave-${evt.id}`}
                      className="px-3 py-1.5 bg-red-600 text-white rounded text-[10px] font-bold active:scale-95">Reject</button>
                  </>
                )}
                <button onClick={() => onDeleteEvent(evt.id)} className="p-1.5 text-slate-400 hover:text-red-400">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
